import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Flame, LogOut, Users, DollarSign, TreePine, Lock, Send, Download, Megaphone, KeyRound, Trash2, AlertTriangle, Rocket, Target, CalendarClock, CalendarDays, Infinity as InfinityIcon, BarChart3, ShieldAlert, MailCheck, Award, Radio, Settings as SettingsIcon, Search, HeartHandshake, Link2, FileCheck2, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminLogin, adminMe, adminGetPledges, adminBroadcast, adminAnnounceLaunch, adminTestLaunch, adminChangePassword, adminClearPledges, adminDeletePledges, adminSendTrialReport, adminClearJunkPledges, adminGetSettings, adminUpdateSettings, getTransparency, adminAddDonation, adminDeleteDonation, getToken, setToken, clearToken } from "@/lib/api";

const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
};

function Login({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminLogin(email.trim(), password);
      setToken(data.access_token);
      toast.success("Welcome back, admin.");
      onSuccess();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="w-full max-w-md bg-card border border-white/10 p-8 sm:p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-2 mb-8">
          <Flame className="w-6 h-6 text-ember" />
          <span className="font-display font-bold text-xl">
            After<span className="text-ember">Burn</span> Admin
          </span>
        </div>
        <div className="space-y-6">
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input"
              className="mt-2 w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors"
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              className="mt-2 w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full bg-ember text-black font-bold py-4 hover:bg-ember-light transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPledges = useCallback(() => {
    return adminGetPledges()
      .then(setData)
      .catch(() => {
        toast.error("Session expired. Please log in again.");
        onLogout();
      })
      .finally(() => setLoading(false));
  }, [onLogout]);

  useEffect(() => {
    loadPledges();
  }, [loadPledges]);

  const trees = data ? Math.floor(data.total_amount / 1.25) : 0;

  // Waitlist goal targets (signups needed to comfortably clear soft/hard caps).
  const SOFT_SIGNUP_GOAL = 300;
  const HARD_SIGNUP_GOAL = 600;
  const signups = data?.count ?? 0;
  const softPct = Math.min(100, Math.round((signups / SOFT_SIGNUP_GOAL) * 100));
  const hardPct = Math.min(100, Math.round((signups / HARD_SIGNUP_GOAL) * 100));

  const stats = [
    { label: "Total Pledges", value: data?.count ?? 0, icon: Users },
    { label: "Total Pledged", value: `$${(data?.total_amount ?? 0).toLocaleString()}`, icon: DollarSign },
    { label: "Trees From Pledges", value: trees.toLocaleString(), icon: TreePine },
  ];

  const signupStats = [
    { label: "Signups Today", value: (data?.today_count ?? 0).toLocaleString(), icon: CalendarClock, hint: "Since midnight UTC" },
    { label: "This Week", value: (data?.week_count ?? 0).toLocaleString(), icon: CalendarDays, hint: "Last 7 days" },
    { label: "All Time", value: (data?.count ?? 0).toLocaleString(), icon: InfinityIcon, hint: "Total waitlist" },
  ];

  const daily = data?.daily ?? [];
  const maxDaily = Math.max(1, ...daily.map((d) => d.count));
  const suspiciousCount = data?.suspicious_count ?? 0;
  const sources = data?.sources ?? {};
  const sourceEntries = Object.entries(sources).sort((a, b) => b[1] - a[1]);
  const sourceTotal = sourceEntries.reduce((sum, [, n]) => sum + n, 0) || 1;
  const scorecard = data?.scorecard ?? null;
  const settings = data?.settings ?? { trial_start: null, report_frequency: "weekly" };

  const [reporting, setReporting] = useState(false);
  const sendTrialReport = async () => {
    setReporting(true);
    try {
      const res = await adminSendTrialReport();
      if (res.sent) {
        toast.success(`Trial report emailed to ${res.to}. Check your inbox.`);
      } else {
        toast.error("Report generated but the email failed to send. Check email settings.");
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send the trial report.");
    } finally {
      setReporting(false);
    }
  };

  const [purgingJunk, setPurgingJunk] = useState(false);
  const purgeJunk = async () => {
    if (suspiciousCount === 0) {
      toast.error("No junk signups flagged right now.");
      return;
    }
    if (!window.confirm(`Permanently delete all ${suspiciousCount} flagged-junk signup(s)? This cannot be undone.`)) {
      return;
    }
    setPurgingJunk(true);
    try {
      const res = await adminClearJunkPledges();
      toast.success(`Removed ${res.deleted} junk signup(s). Your real list is clean.`);
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't purge junk signups.");
    } finally {
      setPurgingJunk(false);
    }
  };

  const [savingTrial, setSavingTrial] = useState(false);
  const startTrialToday = async () => {
    setSavingTrial(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await adminUpdateSettings({ trial_start: today });
      toast.success(`Promoter trial started ${today}. Scorecard is now tracking.`);
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't start the trial.");
    } finally {
      setSavingTrial(false);
    }
  };
  const endTrial = async () => {
    setSavingTrial(true);
    try {
      await adminUpdateSettings({ trial_start: "" });
      toast.success("Trial tracking cleared.");
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't clear the trial.");
    } finally {
      setSavingTrial(false);
    }
  };

  const [savingFreq, setSavingFreq] = useState(false);
  const setFrequency = async (freq) => {
    setSavingFreq(true);
    try {
      await adminUpdateSettings({ report_frequency: freq });
      toast.success(freq === "daily" ? "Switched to a DAILY trial digest." : "Switched to a WEEKLY trial report.");
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't update the schedule.");
    } finally {
      setSavingFreq(false);
    }
  };

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const subscriberCount = data?.count ?? 0;

  const sendBroadcast = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Add a subject and a message first.");
      return;
    }
    if (!window.confirm(`Send this announcement to all your subscribers? This cannot be undone.`)) {
      return;
    }
    setSending(true);
    try {
      const res = await adminBroadcast(subject.trim(), message.trim());
      toast.success(`Sent to ${res.sent} of ${res.recipients} subscribers${res.failed ? ` (${res.failed} failed)` : ""}.`);
      setSubject("");
      setMessage("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Broadcast failed. Try again.");
    } finally {
      setSending(false);
    }
  };

  const [launchUrl, setLaunchUrl] = useState("");
  const [launchSubject, setLaunchSubject] = useState("🔥 The AfterBurn presale is LIVE");
  const [launching, setLaunching] = useState(false);

  const announceLaunch = async () => {
    const url = launchUrl.trim();
    if (!url) {
      toast.error("Paste your live presale URL first.");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    if (subscriberCount === 0) {
      toast.error("There are no subscribers to notify yet.");
      return;
    }
    if (!window.confirm(`Send the "Presale is LIVE" launch email to all ${subscriberCount} subscriber(s)? This cannot be undone.`)) {
      return;
    }
    setLaunching(true);
    try {
      const res = await adminAnnounceLaunch(url, launchSubject.trim() || undefined);
      toast.success(`🚀 Launch sent to ${res.sent} of ${res.recipients} subscribers${res.failed ? ` (${res.failed} failed)` : ""}.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Launch announcement failed. Try again.");
    } finally {
      setLaunching(false);
    }
  };

  const [testing, setTesting] = useState(false);

  const sendTestLaunch = async () => {
    const url = launchUrl.trim();
    if (!url) {
      toast.error("Paste your live presale URL first.");
      return;
    }
    if (!/^https?:\/\//i.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    setTesting(true);
    try {
      const res = await adminTestLaunch(url, launchSubject.trim() || undefined);
      toast.success(`Test email sent to ${res.to}. Check your inbox.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Test send failed. Try again.");
    } finally {
      setTesting(false);
    }
  };

  const exportCsv = () => {
    const rows = data?.pledges || [];
    if (rows.length === 0) {
      toast.error("No subscribers to export yet.");
      return;
    }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = ["Name", "Email", "Source", "Amount", "Date"];
    const lines = rows.map((p) => [esc(p.name), esc(p.email), esc(p.source || "direct"), esc(p.amount || 0), esc(p.created_at)].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `afterburn-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Subscriber CSV downloaded.");
  };

  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);

  const changePassword = async () => {
    if (!curPwd || !newPwd) {
      toast.error("Fill in your current and new password.");
      return;
    }
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("New passwords don't match.");
      return;
    }
    setChangingPwd(true);
    try {
      await adminChangePassword(curPwd, newPwd);
      toast.success("Password changed! Use it next time you log in.");
      setCurPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't change password.");
    } finally {
      setChangingPwd(false);
    }
  };

  const [clearing, setClearing] = useState(false);

  // Donations ledger (public transparency page)
  const emptyDon = { date: new Date().toISOString().slice(0, 10), organization: "", amount_usd: "", amount_eth: "", tx_url: "", receipt_url: "", note: "" };
  const [donations, setDonations] = useState([]);
  const [charity, setCharity] = useState(null);
  const [don, setDon] = useState(emptyDon);
  const [savingDon, setSavingDon] = useState(false);

  const loadDonations = useCallback(() => {
    getTransparency()
      .then((d) => {
        setDonations(d.donations || []);
        setCharity(d);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDonations();
  }, [loadDonations]);

  const addDonation = async () => {
    if (!don.organization.trim()) {
      toast.error("Organization name is required.");
      return;
    }
    setSavingDon(true);
    try {
      await adminAddDonation({
        date: don.date,
        organization: don.organization.trim(),
        amount_usd: don.amount_usd ? Number(don.amount_usd) : undefined,
        amount_eth: don.amount_eth ? Number(don.amount_eth) : undefined,
        tx_url: don.tx_url.trim() || undefined,
        receipt_url: don.receipt_url.trim() || undefined,
        note: don.note.trim() || undefined,
      });
      toast.success("Donation published to the transparency page.");
      setDon(emptyDon);
      loadDonations();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add donation.");
    } finally {
      setSavingDon(false);
    }
  };

  const removeDonation = async (id) => {
    if (!window.confirm("Remove this donation from the public ledger?")) return;
    try {
      await adminDeleteDonation(id);
      toast.success("Donation removed.");
      loadDonations();
    } catch {
      toast.error("Could not remove donation.");
    }
  };

  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [search, setSearch] = useState("");
  const pledgeList = data?.pledges || [];
  const q = search.trim().toLowerCase();
  const filtered = q
    ? pledgeList.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(q) ||
          (p.email || "").toLowerCase().includes(q) ||
          (p.source || "").toLowerCase().includes(q)
      )
    : pledgeList;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allVisibleSelected = filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (filtered.every((p) => prev.has(p.id))) return new Set();
      return new Set(filtered.map((p) => p.id));
    });
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      toast.error("Select at least one subscriber first.");
      return;
    }
    if (!window.confirm(`Permanently delete ${ids.length} selected subscriber(s)? This cannot be undone.`)) {
      return;
    }
    setDeletingSelected(true);
    try {
      const res = await adminDeletePledges(ids);
      toast.success(`Deleted ${res.deleted} subscriber(s).`);
      setSelectedIds(new Set());
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't delete the selected subscribers.");
    } finally {
      setDeletingSelected(false);
    }
  };

  const clearAllSubscribers = async () => {
    const count = data?.count ?? 0;
    if (count === 0) {
      toast.error("There are no subscribers to clear.");
      return;
    }
    if (!window.confirm(`Permanently delete ALL ${count} subscriber(s)? This cannot be undone.`)) {
      return;
    }
    const typed = window.prompt(`Type DELETE to confirm wiping all ${count} subscriber(s).`);
    if (typed !== "DELETE") {
      toast.error("Cancelled — you must type DELETE to confirm.");
      return;
    }
    setClearing(true);
    try {
      const res = await adminClearPledges();
      toast.success(`Cleared ${res.deleted} subscriber(s).`);
      await loadPledges();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't clear subscribers.");
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 max-w-7xl mx-auto py-10">
      <div className="flex items-center justify-between mb-10">
        <div className="flex items-center gap-2">
          <Flame className="w-6 h-6 text-ember" />
          <span className="font-display font-bold text-xl">
            After<span className="text-ember">Burn</span> Admin
          </span>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout-btn"
          className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors"
        >
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </div>

      {/* Signups tracker — perfect for watching the promoter trial */}
      <div className="mb-10" data-testid="signups-tracker">
        <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-ember" />
            <h2 className="font-display font-bold text-xl">Waitlist Signups</h2>
          </div>
          <button
            onClick={sendTrialReport}
            disabled={reporting}
            data-testid="send-trial-report-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors disabled:opacity-60"
          >
            <MailCheck className="w-4 h-4" />
            {reporting ? "Sending..." : "Email me the trial report"}
          </button>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {signupStats.map((s) => (
            <div
              key={s.label}
              className="bg-gradient-to-br from-ember/10 to-transparent border border-ember/30 p-6"
              data-testid={`signup-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{s.label}</span>
                <s.icon className="w-4 h-4 text-ember" />
              </div>
              <div className="mt-3 font-display font-bold text-4xl text-ember" data-testid={`signup-value-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {loading ? "…" : s.value}
              </div>
              <div className="mt-1 font-mono text-[11px] text-muted-foreground/70">{s.hint}</div>
            </div>
          ))}
        </div>

        {/* 7-day trend bar chart */}
        <div className="mt-6 border border-white/10 bg-card p-6 sm:p-8" data-testid="daily-signups-chart">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="w-5 h-5 text-ember" />
            <h3 className="font-display font-bold text-lg">Last 7 Days</h3>
            {suspiciousCount > 0 && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-mono text-red-400 border border-red-500/40 bg-red-500/10 px-2.5 py-1" data-testid="suspicious-count-badge">
                <ShieldAlert className="w-3.5 h-3.5" /> {suspiciousCount} flagged as junk
              </span>
            )}
            {suspiciousCount > 0 && (
              <button
                onClick={purgeJunk}
                disabled={purgingJunk}
                data-testid="purge-junk-btn"
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase text-red-400 border border-red-500/50 px-2.5 py-1 hover:bg-red-500 hover:text-black transition-colors disabled:opacity-60"
              >
                <Trash2 className="w-3.5 h-3.5" /> {purgingJunk ? "Purging..." : "Purge junk"}
              </button>
            )}
          </div>
          <div className="flex items-end justify-between gap-2 h-40" data-testid="chart-bars">
            {daily.map((d, i) => {
              const pct = maxDaily > 0 ? Math.round((d.count / maxDaily) * 100) : 0;
              const label = new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short" });
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`chart-bar-${i}`}>
                  <span className="font-mono text-xs text-ember font-bold mb-1">{d.count}</span>
                  <div
                    className="w-full max-w-[42px] bg-gradient-to-t from-ember to-ember-light transition-all duration-700 rounded-sm"
                    style={{ height: `${Math.max(4, pct)}%` }}
                    title={`${d.count} signups`}
                  />
                  <span className="font-mono text-[10px] text-muted-foreground mt-2">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Promoter scorecard + trial settings + source breakdown */}
      <div className="grid lg:grid-cols-2 gap-6 mb-10">
        <div className="border border-ember/40 bg-gradient-to-br from-ember/10 to-transparent p-6 sm:p-8" data-testid="promoter-scorecard">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-ember" />
            <h2 className="font-display font-bold text-xl">Promoter Scorecard</h2>
          </div>
          {scorecard ? (
            <>
              <div className="grid grid-cols-3 gap-4 mb-5">
                <div className="text-center">
                  <div className="font-display font-bold text-3xl text-emerald-400" data-testid="scorecard-clean">{scorecard.clean.toLocaleString()}</div>
                  <div className="font-mono text-[11px] uppercase text-muted-foreground mt-1">Clean signups</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-3xl text-red-400" data-testid="scorecard-junk">{scorecard.junk.toLocaleString()}</div>
                  <div className="font-mono text-[11px] uppercase text-muted-foreground mt-1">Junk</div>
                </div>
                <div className="text-center">
                  <div className="font-display font-bold text-3xl text-ember" data-testid="scorecard-total">{scorecard.total.toLocaleString()}</div>
                  <div className="font-mono text-[11px] uppercase text-muted-foreground mt-1">Total</div>
                </div>
              </div>
              <p className="font-mono text-[11px] text-muted-foreground/70 mb-4">
                Trial started {scorecard.trial_start}. "Clean signups" is the real number to judge a renewal on.
              </p>
              <button
                onClick={endTrial}
                disabled={savingTrial}
                data-testid="end-trial-btn"
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors disabled:opacity-60"
              >
                {savingTrial ? "Saving..." : "Clear trial tracking"}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-5">
                Start a trial to track exactly how many <span className="text-emerald-400">clean</span> signups a promoter drives from today onward — so you can judge if they earned a renewal.
              </p>
              <button
                onClick={startTrialToday}
                disabled={savingTrial}
                data-testid="start-trial-btn"
                className="inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3 hover:bg-ember-light transition-colors disabled:opacity-60"
              >
                <Award className="w-4 h-4" /> {savingTrial ? "Starting..." : "Start promoter trial today"}
              </button>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <SettingsIcon className="w-4 h-4 text-ember" />
              <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Trial report schedule</h3>
            </div>
            <div className="inline-flex border border-white/15" data-testid="report-frequency-toggle">
              <button
                onClick={() => setFrequency("weekly")}
                disabled={savingFreq}
                data-testid="freq-weekly-btn"
                className={`px-4 py-2 font-mono text-xs uppercase transition-colors disabled:opacity-60 ${settings.report_frequency === "weekly" ? "bg-ember text-black font-bold" : "text-muted-foreground hover:text-ember"}`}
              >
                Weekly
              </button>
              <button
                onClick={() => setFrequency("daily")}
                disabled={savingFreq}
                data-testid="freq-daily-btn"
                className={`px-4 py-2 font-mono text-xs uppercase transition-colors disabled:opacity-60 ${settings.report_frequency === "daily" ? "bg-ember text-black font-bold" : "text-muted-foreground hover:text-ember"}`}
              >
                Daily
              </button>
            </div>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground/70">
              Auto-emails you at 09:00 UTC — {settings.report_frequency === "daily" ? "every day (great during an active trial week)" : "every Monday"}.
            </p>
          </div>
        </div>

        <div className="border border-white/10 bg-card p-6 sm:p-8" data-testid="source-breakdown">
          <div className="flex items-center gap-2 mb-5">
            <Radio className="w-5 h-5 text-ember" />
            <h2 className="font-display font-bold text-xl">Signup Sources</h2>
          </div>
          {sourceEntries.length === 0 ? (
            <p className="text-sm text-muted-foreground">No signups yet — sources will show here once people join.</p>
          ) : (
            <div className="space-y-4">
              {sourceEntries.map(([src, n]) => {
                const pct = Math.round((n / sourceTotal) * 100);
                const label = src === "x" ? "X (Twitter)" : src.charAt(0).toUpperCase() + src.slice(1);
                return (
                  <div key={src} data-testid={`source-row-${src}`}>
                    <div className="flex justify-between font-mono text-xs mb-1.5">
                      <span className="text-foreground">{label}</span>
                      <span className="text-ember font-semibold">{n} · {pct}%</span>
                    </div>
                    <div className="h-2.5 bg-background border border-white/10 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-forest-light via-ember to-ember-light transition-all duration-700" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
              <p className="mt-4 font-mono text-[11px] text-muted-foreground/70">
                Add <span className="text-ember">?ref=x</span> or <span className="text-ember">?ref=telegram</span> to links you share so signups get attributed to the right channel.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-6 mb-10">
        {stats.map((s) => (
          <div key={s.label} className="bg-card border border-white/10 p-6" data-testid={`admin-stat-${s.label}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <s.icon className="w-4 h-4 text-ember" />
            </div>
            <div className="mt-3 font-display font-bold text-3xl text-ember">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Waitlist goal tracker */}
      <div className="border border-white/10 bg-card p-6 sm:p-8 mb-10" data-testid="waitlist-goal-tracker">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-ember" />
          <h2 className="font-display font-bold text-xl">Waitlist Goal Tracker</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          <span className="text-ember font-semibold">{signups.toLocaleString()}</span> signups so far.
          Targets estimate the waitlist size needed to comfortably clear each presale cap.
        </p>
        <div className="space-y-6">
          <div data-testid="soft-goal-progress">
            <div className="flex justify-between font-mono text-xs uppercase tracking-wider mb-2">
              <span className="text-muted-foreground">Soft-cap target ({SOFT_SIGNUP_GOAL} signups)</span>
              <span className="text-ember font-semibold">{signups}/{SOFT_SIGNUP_GOAL} · {softPct}%</span>
            </div>
            <div className="h-3 bg-background border border-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-forest-light via-ember to-ember-light transition-all duration-700" style={{ width: `${softPct}%` }} />
            </div>
          </div>
          <div data-testid="hard-goal-progress">
            <div className="flex justify-between font-mono text-xs uppercase tracking-wider mb-2">
              <span className="text-muted-foreground">Hard-cap target ({HARD_SIGNUP_GOAL} signups)</span>
              <span className="text-ember font-semibold">{signups}/{HARD_SIGNUP_GOAL} · {hardPct}%</span>
            </div>
            <div className="h-3 bg-background border border-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-forest-light via-ember to-ember-light transition-all duration-700" style={{ width: `${hardPct}%` }} />
            </div>
          </div>
        </div>
        <p className="mt-5 font-mono text-[11px] text-muted-foreground/70">
          Based on ~0.2 ETH avg buy & ~15% signup→buyer conversion. Ask to adjust these targets anytime.
        </p>
      </div>

      {/* One-click presale launch blast */}
      <div className="border border-ember/40 bg-gradient-to-br from-ember/10 to-transparent p-6 sm:p-8 mb-10" data-testid="launch-panel">
        <div className="flex items-center gap-2 mb-2">
          <Rocket className="w-5 h-5 text-ember" />
          <h2 className="font-display font-bold text-xl">Announce Presale is LIVE</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          The moment your presale opens, paste the live buy link below and hit the button — a pre-designed launch email
          fires instantly to all <span className="text-ember font-semibold">{subscriberCount}</span> subscriber(s).
        </p>
        <div className="space-y-4">
          <input
            type="url"
            value={launchUrl}
            onChange={(e) => setLaunchUrl(e.target.value)}
            placeholder="Live presale URL — e.g. https://pinksale.finance/... or your DEX pool link"
            data-testid="launch-url-input"
            className="w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
          <input
            type="text"
            value={launchSubject}
            onChange={(e) => setLaunchSubject(e.target.value)}
            placeholder="Email subject"
            data-testid="launch-subject-input"
            className="w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={announceLaunch}
              disabled={launching}
              data-testid="launch-send-btn"
              className="inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3 hover:bg-ember-light transition-colors disabled:opacity-60"
            >
              <Rocket className="w-4 h-4" />
              {launching ? "Launching..." : "🚀 Announce Presale is LIVE"}
            </button>
            <button
              onClick={sendTestLaunch}
              disabled={testing}
              data-testid="launch-test-btn"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {testing ? "Sending..." : "Send test to myself"}
            </button>
          </div>
        </div>
      </div>

      {/* Launch announcement broadcast */}
      <div className="border border-white/10 bg-card p-6 sm:p-8 mb-10" data-testid="broadcast-panel">
        <div className="flex items-center gap-2 mb-2">
          <Megaphone className="w-5 h-5 text-ember" />
          <h2 className="font-display font-bold text-xl">General Announcement</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Send any custom message to all <span className="text-ember font-semibold">{subscriberCount}</span> subscriber(s) — updates, reminders, or news.
        </p>
        <div className="space-y-4">
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject — e.g. The AFTBRN presale is LIVE 🔥"
            data-testid="broadcast-subject-input"
            className="w-full bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            placeholder="Your message to subscribers... (line breaks are preserved)"
            data-testid="broadcast-message-input"
            className="w-full bg-background border border-white/10 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50 resize-y"
          />
          <button
            onClick={sendBroadcast}
            disabled={sending}
            data-testid="broadcast-send-btn"
            className="inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3 hover:bg-ember-light transition-colors disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sending..." : "Send Announcement"}
          </button>
        </div>
      </div>

      {/* Change admin password */}
      <div className="border border-white/10 bg-card p-6 sm:p-8 mb-10" data-testid="change-password-panel">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-5 h-5 text-ember" />
          <h2 className="font-display font-bold text-xl">Change Password</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6">
          Update your admin password here — it applies immediately, no redeploy needed.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          <input
            type="password"
            value={curPwd}
            onChange={(e) => setCurPwd(e.target.value)}
            placeholder="Current password"
            data-testid="cur-password-input"
            className="bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
          <input
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            placeholder="New password (min 8 chars)"
            data-testid="new-password-input"
            className="bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
          <input
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            placeholder="Confirm new password"
            data-testid="confirm-password-input"
            className="bg-background border-b border-muted-foreground/60 px-4 py-3 focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
          />
        </div>
        <button
          onClick={changePassword}
          disabled={changingPwd}
          data-testid="change-password-btn"
          className="mt-6 inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3 hover:bg-ember-light transition-colors disabled:opacity-60"
        >
          <KeyRound className="w-4 h-4" />
          {changingPwd ? "Updating..." : "Update Password"}
        </button>
      </div>

      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display font-bold text-xl">Subscribers</h2>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <button
              onClick={deleteSelected}
              disabled={deletingSelected}
              data-testid="delete-selected-btn"
              className="inline-flex items-center gap-2 border border-red-500/50 text-red-400 px-4 py-2 font-mono text-xs uppercase hover:bg-red-500 hover:text-black transition-colors disabled:opacity-60"
            >
              <Trash2 className="w-4 h-4" /> {deletingSelected ? "Deleting..." : `Delete selected (${selectedIds.size})`}
            </button>
          )}
          <button
            onClick={exportCsv}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase hover:border-ember hover:text-ember transition-colors"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      <div className="relative mb-4" data-testid="subscriber-search-wrap">
        <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email or source…"
          data-testid="subscriber-search-input"
          className="w-full bg-background border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/50"
        />
        {q && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground" data-testid="subscriber-search-count">
            {filtered.length} match{filtered.length === 1 ? "" : "es"}
          </span>
        )}
      </div>

      <div className="border border-white/10 bg-card/50 overflow-x-auto" data-testid="admin-pledges-table">
        <table className="w-full font-mono text-sm min-w-[680px]">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-white/10 text-xs uppercase tracking-wider">
              <th className="px-5 py-4 w-10">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                  className="w-4 h-4 accent-ember cursor-pointer"
                  aria-label="Select all subscribers"
                />
              </th>
              <th className="px-5 py-4">#</th>
              <th className="px-5 py-4">Name</th>
              <th className="px-5 py-4">Email</th>
              <th className="px-5 py-4">Source</th>
              <th className="px-5 py-4 text-right">Amount</th>
              <th className="px-5 py-4 text-right">Date</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${selectedIds.has(p.id) ? "bg-ember/5" : p.suspicious ? "bg-red-500/5" : ""}`} data-testid={`admin-pledge-row-${i}`}>
                <td className="px-5 py-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleSelect(p.id)}
                    data-testid={`pledge-checkbox-${i}`}
                    className="w-4 h-4 accent-ember cursor-pointer"
                    aria-label={`Select ${p.name}`}
                  />
                </td>
                <td className="px-5 py-4 text-muted-foreground">{i + 1}</td>
                <td className="px-5 py-4 font-semibold">
                  <div className="flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.suspicious && (
                      <span
                        title={`Flagged: ${(p.flags || []).join(", ")}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-red-400 border border-red-500/40 bg-red-500/10 px-1.5 py-0.5"
                        data-testid={`pledge-junk-badge-${i}`}
                      >
                        <ShieldAlert className="w-3 h-3" /> Junk
                      </span>
                    )}
                  </div>
                  {p.suspicious && (
                    <div className="mt-1 text-[10px] text-red-400/80 font-normal">{(p.flags || []).join(" · ")}</div>
                  )}
                </td>
                <td className="px-5 py-4 text-muted-foreground">{p.email}</td>
                <td className="px-5 py-4">
                  <span className="inline-block text-[10px] font-mono uppercase tracking-wider text-ember border border-ember/30 bg-ember/5 px-2 py-0.5" data-testid={`pledge-source-${i}`}>
                    {(p.source || "direct") === "x" ? "X" : (p.source || "direct")}
                  </span>
                </td>
                <td className="px-5 py-4 text-right text-ember">${(p.amount || 0).toLocaleString()}</td>
                <td className="px-5 py-4 text-right text-muted-foreground">{fmtDate(p.created_at)}</td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">
                  {q ? `No subscribers match "${search.trim()}".` : "No pledges yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Donations Ledger — publishes to the public /transparency page */}
      <div className="mt-10 border border-white/10 bg-card/40 p-6" data-testid="donations-manager">
        <div className="flex items-center gap-2 mb-2">
          <HeartHandshake className="w-5 h-5 text-ember" />
          <h2 className="font-display font-bold text-xl">Donations Ledger</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-6 max-w-2xl">
          Log each donation you make to a wildfire-recovery org. It appears instantly on your public{" "}
          <a href="/transparency" target="_blank" rel="noreferrer" className="text-ember hover:underline">Transparency page</a>.
          Add the BaseScan link (crypto leaving the wallet) and the receipt link (money arriving) so supporters can verify both ends.
        </p>

        {charity && (
          <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm font-mono border border-white/10 bg-background px-4 py-3" data-testid="charity-balance-summary">
            <span className="text-muted-foreground">Charity wallet:</span>
            <span className="text-ember">{charity.balance_eth != null ? `${charity.balance_eth} ETH` : "—"}</span>
            <span className="text-muted-foreground">≈ {charity.balance_usd != null ? `$${charity.balance_usd.toLocaleString()}` : "—"}</span>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Date</label>
            <input type="date" value={don.date} onChange={(e) => setDon({ ...don, date: e.target.value })} data-testid="donation-date-input"
              className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors" />
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Organization *</label>
            <input type="text" value={don.organization} onChange={(e) => setDon({ ...don, organization: e.target.value })} placeholder="e.g. One Tree Planted" data-testid="donation-org-input"
              className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Amount (USD)</label>
              <input type="number" min="0" step="any" value={don.amount_usd} onChange={(e) => setDon({ ...don, amount_usd: e.target.value })} placeholder="500" data-testid="donation-usd-input"
                className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Amount (ETH)</label>
              <input type="number" min="0" step="any" value={don.amount_eth} onChange={(e) => setDon({ ...don, amount_eth: e.target.value })} placeholder="0.15" data-testid="donation-eth-input"
                className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">BaseScan tx link</label>
            <input type="url" value={don.tx_url} onChange={(e) => setDon({ ...don, tx_url: e.target.value })} placeholder="https://basescan.org/tx/0x…" data-testid="donation-tx-input"
              className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
          </div>
          <div>
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Receipt link</label>
            <input type="url" value={don.receipt_url} onChange={(e) => setDon({ ...don, receipt_url: e.target.value })} placeholder="https://… (receipt image/pdf)" data-testid="donation-receipt-input"
              className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Note (optional)</label>
            <input type="text" value={don.note} onChange={(e) => setDon({ ...don, note: e.target.value })} placeholder="Reforestation after the 2026 fires" data-testid="donation-note-input"
              className="mt-2 w-full bg-background border border-white/10 px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-ember transition-colors placeholder:text-muted-foreground/40" />
          </div>
        </div>

        <button onClick={addDonation} disabled={savingDon} data-testid="add-donation-btn"
          className="mt-6 inline-flex items-center gap-2 bg-ember text-black font-bold px-6 py-3 hover:bg-ember-light transition-colors disabled:opacity-60">
          <Plus className="w-4 h-4" /> {savingDon ? "Publishing…" : "Publish Donation"}
        </button>

        {donations.length > 0 && (
          <div className="mt-8 border border-white/10 bg-background overflow-x-auto" data-testid="admin-donations-table">
            <table className="w-full font-mono text-sm min-w-[620px]">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-white/10 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3 text-center">Proof</th>
                  <th className="px-4 py-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {donations.map((d, i) => (
                  <tr key={d.id} className="border-b border-white/5" data-testid={`admin-donation-row-${i}`}>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{d.date}</td>
                    <td className="px-4 py-3 font-semibold">{d.organization}</td>
                    <td className="px-4 py-3 text-right text-ember whitespace-nowrap">
                      {d.amount_usd != null ? `$${d.amount_usd.toLocaleString()}` : ""}
                      {d.amount_eth != null ? <span className="text-muted-foreground text-xs">{d.amount_usd != null ? " · " : ""}{d.amount_eth} ETH</span> : ""}
                      {d.amount_usd == null && d.amount_eth == null ? "—" : ""}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        {d.tx_url && <a href={d.tx_url} target="_blank" rel="noreferrer" title="On-chain tx" className="text-muted-foreground hover:text-ember"><Link2 className="w-4 h-4" /></a>}
                        {d.receipt_url && <a href={d.receipt_url} target="_blank" rel="noreferrer" title="Receipt" className="text-muted-foreground hover:text-ember"><FileCheck2 className="w-4 h-4" /></a>}
                        {!d.tx_url && !d.receipt_url && <span className="text-muted-foreground">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeDonation(d.id)} data-testid={`delete-donation-btn-${i}`} className="text-red-400/70 hover:text-red-400 transition-colors" aria-label="Remove donation">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-10 border border-red-500/30 bg-red-500/5 p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <h2 className="font-display font-bold text-xl text-red-400">Danger Zone</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5 max-w-xl">
          Permanently delete every subscriber and pledge from the database. Use this to wipe test data before launch. This action cannot be undone.
        </p>
        <button
          onClick={clearAllSubscribers}
          disabled={clearing}
          data-testid="clear-all-subscribers-btn"
          className="inline-flex items-center gap-2 border border-red-500/50 text-red-400 px-6 py-3 font-mono text-xs uppercase hover:bg-red-500 hover:text-black transition-colors disabled:opacity-60"
        >
          <Trash2 className="w-4 h-4" />
          {clearing ? "Clearing..." : "Clear All Subscribers"}
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      setAuthed(false);
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        setAuthed(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setAuthed(false);
  };

  if (authed === null) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground font-mono">Loading…</div>;
  }

  return (
    <main className="bg-background text-foreground min-h-screen">
      {authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />}
    </main>
  );
}
